import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import {
  Banknote, AlertTriangle, Calendar, Percent, DollarSign, TrendingUp,
  FileText, Loader2,
} from 'lucide-react';

const money0 = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(n || 0);
const money2 = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
}).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const fmtPct = (r) => `${(r * 100).toFixed(2)}%`;


/**
 * ActiveLoansPanel — active loan cards + separation warning.
 * Mounted in the Withdrawals & Loans tab below the bank accounts panel.
 */
export const ActiveLoansPanel = () => {
  const [loans, setLoans] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true); setErr('');
      try {
        const [lRes, pRes] = await Promise.allSettled([
          api.get('/participant/loans'),
          api.get('/participant/profile'),
        ]);
        if (cancelled) return;
        if (lRes.status === 'fulfilled') setLoans(lRes.value.data || []);
        else setErr(lRes.reason?.response?.data?.detail || 'Unable to load loans.');
        if (pRes.status === 'fulfilled') setProfile(pRes.value.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const totalOutstanding = loans.reduce((sum, l) => sum + (l.balance || 0), 0);
  const separationDate = profile?.separationDate || null;
  const showSeparationWarning = Boolean(separationDate) && totalOutstanding > 0;

  return (
    <div className="space-y-3" data-testid="active-loans-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wider">
          Active Loans
        </h2>
        {!loading && loans.length > 0 && (
          <span className="text-[10px] font-mono text-[#6B7280]" data-testid="loans-summary">
            {loans.length} active · Total balance {money2(totalOutstanding)}
          </span>
        )}
      </div>

      {/* Separation warning (only shows when both conditions are met) */}
      {showSeparationWarning && (
        <div
          className="panel p-4 bg-red-50 border-red-200"
          data-testid="separation-warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
            <div className="text-xs text-red-800 leading-relaxed">
              <p className="font-semibold mb-1">Upcoming Separation — Action Required</p>
              <p>
                You have an outstanding UniFed Core loan balance of{' '}
                <b data-testid="separation-warning-balance">{money2(totalOutstanding)}</b>.
                You must repay this balance within <b>90 days</b> of your separation date of{' '}
                <b data-testid="separation-warning-date">{fmtDate(separationDate)}</b>{' '}
                to avoid a taxable distribution. Unpaid balance will be reported as taxable
                income and may be subject to a <b>10% early withdrawal penalty</b> if under
                age 59½. (26 U.S.C. § 72(p); 5 C.F.R. Part 1655)
              </p>
            </div>
          </div>
        </div>
      )}

      {err && !loading && (
        <div className="panel p-3 border-red-200 bg-red-50" data-testid="active-loans-error">
          <p className="text-xs text-red-700">{err}</p>
        </div>
      )}

      {loading ? (
        <div className="panel p-6 animate-pulse" data-testid="active-loans-loading">
          <div className="h-4 bg-gray-100 rounded w-40 mb-3" />
          <div className="h-28 bg-gray-50 rounded" />
        </div>
      ) : loans.length === 0 ? (
        <div className="panel p-8 text-center" data-testid="no-active-loans">
          <FileText size={28} className="mx-auto text-[#D1D5DB] mb-2" />
          <p className="text-sm text-[#6B7280]">No active loans on record.</p>
          <p className="text-[11px] text-[#9CA3AF] mt-1">
            Loan applications appear here once approved and disbursed.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {loans.map((loan) => {
            const repaidPct = loan.original > 0
              ? Math.min((loan.amountRepaid / loan.original) * 100, 100)
              : 0;
            const typeColor = loan.type === 'Residential' ? '#2D5F8A' : '#1B2A4A';
            return (
              <LoanCard
                key={loan.id}
                loan={loan}
                repaidPct={repaidPct}
                typeColor={typeColor}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};


function LoanCard({ loan, repaidPct, typeColor }) {
  return (
    <div
      className="panel p-5"
      data-testid={`loan-card-${loan.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded flex items-center justify-center"
            style={{ backgroundColor: typeColor }}
          >
            <Banknote size={16} className="text-white" />
          </div>
          <div>
            <span
              className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded"
              style={{
                backgroundColor: `${typeColor}15`,
                color: typeColor,
                textTransform: 'uppercase',
              }}
              data-testid={`loan-type-${loan.id}`}
            >
              {loan.type}
            </span>
            <p className="text-xs text-[#6B7280] mt-1 font-mono" data-testid={`loan-number-${loan.id}`}>
              {loan.loanNumber || loan.id.slice(0, 8)}
            </p>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded border bg-emerald-50 border-emerald-200 text-emerald-700"
          data-testid={`loan-status-${loan.id}`}
        >
          {(loan.status || 'active').toUpperCase()}
        </span>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">
          <span>Repayment Progress</span>
          <span className="font-mono font-medium text-[#1B2A4A]" data-testid={`loan-progress-pct-${loan.id}`}>
            {repaidPct.toFixed(1)}%
          </span>
        </div>
        <div className="w-full h-2.5 bg-[#F0F1F3] rounded-sm overflow-hidden">
          <div
            className="h-full rounded-sm transition-all"
            style={{ width: `${repaidPct}%`, backgroundColor: typeColor }}
            data-testid={`loan-progress-bar-${loan.id}`}
          />
        </div>
        <div className="flex items-center justify-between text-[10px] font-mono text-[#9CA3AF] mt-1">
          <span>
            Repaid <b className="text-[#1B2A4A]" data-testid={`loan-repaid-${loan.id}`}>
              {money2(loan.amountRepaid)}
            </b>
          </span>
          <span>
            of <b className="text-[#1B2A4A]">{money2(loan.original)}</b>
          </span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatBlock
          label="Original Amount"
          value={money0(loan.original)}
          icon={DollarSign}
          testId={`loan-original-${loan.id}`}
        />
        <StatBlock
          label="Outstanding Balance"
          value={money0(loan.balance)}
          icon={TrendingUp}
          highlight
          testId={`loan-balance-${loan.id}`}
        />
        <StatBlock
          label="Monthly Payment"
          value={money2(loan.monthlyPayment)}
          icon={Calendar}
          testId={`loan-monthly-${loan.id}`}
        />
        <StatBlock
          label="Interest Rate"
          value={fmtPct(loan.rate)}
          sub="A Fund at origination"
          icon={Percent}
          testId={`loan-rate-${loan.id}`}
        />
      </div>

      {/* Dates */}
      <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded px-4 py-3 text-xs grid grid-cols-2 gap-3">
        <div>
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block">Originated</span>
          <span className="font-mono text-[#1B2A4A]" data-testid={`loan-origination-${loan.id}`}>
            {fmtDate(loan.originationDate)}
          </span>
        </div>
        <div>
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block">Projected Payoff</span>
          <span className="font-mono text-[#1B2A4A]" data-testid={`loan-payoff-${loan.id}`}>
            {fmtDate(loan.payoffDate)}
          </span>
        </div>
      </div>
    </div>
  );
}


function StatBlock({ label, value, sub, icon: Icon, highlight, testId }) {
  return (
    <div
      className={
        'rounded p-3 border ' +
        (highlight ? 'bg-[#1B2A4A] border-[#1B2A4A]' : 'bg-white border-[#E2E5E9]')
      }
    >
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && (
          <Icon
            size={10}
            className={highlight ? 'text-[#B8C5D8]' : 'text-[#9CA3AF]'}
          />
        )}
        <span
          className={
            'text-[10px] uppercase tracking-wider ' +
            (highlight ? 'text-[#B8C5D8]' : 'text-[#9CA3AF]')
          }
        >
          {label}
        </span>
      </div>
      <div
        className={
          'font-mono font-bold text-lg ' + (highlight ? 'text-white' : 'text-[#1B2A4A]')
        }
        data-testid={testId}
      >
        {value}
      </div>
      {sub && (
        <span
          className={
            'text-[9px] font-mono ' + (highlight ? 'text-[#B8C5D8]' : 'text-[#9CA3AF]')
          }
        >
          {sub}
        </span>
      )}
    </div>
  );
}

export default ActiveLoansPanel;
